import { Component, OnInit, effect, ChangeDetectionStrategy } from '@angular/core';
import { BackendService } from '@shared/backend.service';
import { Chart, ChartConfiguration, LinearScale, CategoryScale } from 'chart.js';
import { ProjectService } from '@app/shell/header/header.project.service';
import { BoxPlotController, ViolinController, Violin } from '@sgratzl/chartjs-chart-boxplot';
import { FLAIRR_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';
import { TableExportService } from '@shared/table-export.service';
import type { ExportColumn, ExportRow } from '@shared/table-export.service';
import {
  normalizeNumericTableRows,
  isNumericTableColumn,
  SMALL_TABLE_FILTER_BUTTON_PROPS,
} from '@shared/table-filter.utils';
import { FilterMatchMode } from '@openng/optimus-ui/api';
import type { FilterMetadata } from '@openng/optimus-ui/api';
import type { Table, TableFilterEvent } from '@openng/optimus-ui/table';

type TableFilters = Record<string, FilterMetadata | FilterMetadata[]>;

// Build stacked barchart data for allele usage
interface AlleleUsageRow {
  gene: string;
  gene_type: string;
  vdjbase_allele: string;
  frequency: string;
  sample_name: string;
}

interface BarDataset {
  label: string;
  data: number[];
  stack: string;
}

interface BarData {
  labels: string[];
  datasets: BarDataset[];
}

interface ViolinDataset {
  label: string;
  data: number[][];
}

interface ViolinData {
  labels: string[];
  datasets: ViolinDataset[];
}

@Component({
  // This component is activated through routing and is not used as an element selector.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'allele-usage',
  templateUrl: './allele-usage.component.html',
  styleUrl: './allele-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlleleUsageComponent implements OnInit {
  isLoading = false;
  public projectViewData: any = null;
  selectedLocus: string = '';
  public barChartPlugins = [];
  public selectedProjectID: string = '';

  // Navigation configuration - allele-usage is for FLAIRR analysis only
  get navConfig(): NavConfig {
    return FLAIRR_NAV_CONFIG;
  }

  get analysisType(): 'capture' | 'flairr' {
    return 'flairr';
  }

  get globalFilterFields(): string[] {
    return this.usageColumns.map((column) => column.data);
  }

  // Allele Usage table and chart
  showAlleleUsage = true;
  searchActive = false;
  compareSelectedRows = false;
  showViolinPlots = false;
  alleleExpressionStyle = 'display: none;';
  public alleleUsagePlotData: any = null;
  readonly pageSizes = [10, 20, 50, 200, 500];
  usageColumns: ExportColumn[] = [];
  usageRows: ExportRow[] = [];
  usageFilters: TableFilters = this.createTableFilters();
  globalFilter = '';
  filteredUsageRows: ExportRow[] = [];
  exportMessage = '';
  readonly filterButtonProps = SMALL_TABLE_FILTER_BUTTON_PROPS;
  public graph = {
    data: [
      { x: [1, 2, 3], y: [2, 6, 3], type: 'scatter', mode: 'lines+points', marker: { color: 'red' } },
      { x: [1, 2, 3], y: [2, 5, 3], type: 'bar' },
    ],
    layout: { width: 320, height: 240, title: 'A Fancy Plot' },
  };

  constructor(
    private backendService: BackendService,
    public projectService: ProjectService,
    private exportService: TableExportService,
  ) {
    effect(() => {
      this.setProject();
    }, {});
  }

  ngOnInit(): void {
    Chart.register(BoxPlotController, ViolinController, Violin, LinearScale, CategoryScale);
  }

  // Haplotype / novel allele charts

  // Project / locus selection

  setProject() {
    if (this.projectService.selectedProjectDetails() !== null) {
      const projectDetails = this.projectService.selectedProjectDetails();
      this.selectedProjectID = projectDetails ? projectDetails.project_id : '';

      this.isLoading = true;
      console.log('getting project view data');
      this.backendService.getProjectViewData(this.selectedProjectID, 'FLAIRR-Seq').subscribe((response: any) => {
        if (response.status === 200) {
          console.log('got project view data');
          this.isLoading = false;
          this.projectViewData = response;
          this.selectLocus(this.projectViewData.loci_covered[0]);
          console.log('back from select locus');
        } else {
          this.isLoading = false;
        }
      });
    }
  }

  selectLocus(locus: string) {
    this.selectedLocus = locus;
    this.alleleUsageUpdate();
  }

  onSelectedLocus(event: Event) {
    this.selectedLocus = (event.target as HTMLSelectElement).value;
    this.alleleUsageUpdate();
  }

  onCompareSelectedRows(event: any) {
    this.compareSelectedRows = event.target.checked;
    console.log('compare selected rows: ' + this.compareSelectedRows);
    this.alleleUsageUpdateHandler();
  }

  onToggleViolinPlots(event: any) {
    this.showViolinPlots = event.target.checked;
    console.log('show violin plots: ' + this.showViolinPlots);
    this.alleleUsageUpdateHandler();
  }

  onChartTypeChange(event: any) {
    this.showViolinPlots = event.target.value === 'violin';
    console.log('chart type changed to: ' + event.target.value);
    this.alleleUsageUpdateHandler();
  }

  // Allele Usage table and chart

  // Function called when the project or locus changes
  alleleUsageUpdate() {
    console.log('alleleUsageUpdate');

    this.isLoading = true;
    this.backendService
      .getProjectAlleleUsage(this.selectedProjectID, 'FLAIRR-Seq', this.selectedLocus)
      .subscribe((response: any) => {
        this.isLoading = false;

        if (response.status === 200) {
          this.usageColumns = response.cols;
          this.usageRows = normalizeNumericTableRows(this.usageColumns, response.data);
          this.filteredUsageRows = this.usageRows;
          this.usageFilters = this.createTableFilters();
          this.globalFilter = '';
          this.showAlleleUsage = true;
          this.alleleUsageUpdateHandler();

          console.log('changed locus');
          this.alleleExpressionStyle = 'display: block;';
        } else {
          this.alleleExpressionStyle = 'display: none;';
        }
      });
  }

  isNumericColumn(column: ExportColumn): boolean {
    return isNumericTableColumn(column, this.usageRows);
  }

  onGlobalFilterChange(event: Event, table: Table): void {
    this.globalFilter = (event.target as HTMLInputElement).value;
    table.filterGlobal(this.globalFilter, FilterMatchMode.CONTAINS);
  }

  alleleUsageUpdateHandler(event?: TableFilterEvent): void {
    if (event) {
      this.filteredUsageRows = (event.filteredValue as ExportRow[] | undefined) ?? this.usageRows;
    }
    const filteredRows = this.filteredUsageRows;
    const selectedRows = new Set<ExportRow>(filteredRows);
    const rows = filteredRows as unknown as AlleleUsageRow[];
    const excludedRows = this.usageRows.filter((row) => !selectedRows.has(row)) as unknown as AlleleUsageRow[];
    this.searchActive = excludedRows.length > 0;

    if (this.searchActive && this.compareSelectedRows) {
      if (this.showViolinPlots) {
        this.alleleUsagePlotData = this.comparativeAlleleViolinUsageData(rows, excludedRows);
      } else {
        this.alleleUsagePlotData = this.comparativeAlleleBarUsageData(rows, excludedRows);
      }
    } else if (this.showViolinPlots) {
      this.alleleUsagePlotData = this.alleleViolinUsageData(rows);
    } else {
      this.alleleUsagePlotData = this.alleleBarUsageData(rows);
    }
  }

  async copyUsageTable(): Promise<void> {
    await this.runTableExport('copy', () => this.exportService.copy(this.usageColumns, this.filteredUsageRows));
  }

  async exportUsageExcel(): Promise<void> {
    await this.runTableExport('Excel', () =>
      this.exportService.exportExcel(this.usageColumns, this.filteredUsageRows, 'allele-usage'),
    );
  }

  async exportUsagePdf(): Promise<void> {
    await this.runTableExport('PDF', () =>
      this.exportService.exportPdf(this.usageColumns, this.filteredUsageRows, 'allele-usage'),
    );
  }

  private async runTableExport(label: string, action: () => Promise<void>): Promise<void> {
    this.exportMessage = '';
    try {
      await action();
      this.exportMessage = label === 'copy' ? 'Table copied to clipboard.' : `${label} export downloaded.`;
    } catch (error) {
      this.exportMessage = `Could not complete ${label === 'copy' ? 'copy' : `${label} export`}.`;
      console.error(`Failed to complete ${label} for allele usage data.`, error);
    }
  }

  private createTableFilters(): TableFilters {
    return { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
  }

  // --- Generic utilities for manipulating allele data

  // Method to get the keys of alleleUsagePlotData
  getalleleUsageGeneTypes(): string[] {
    return this.alleleUsagePlotData ? Object.keys(this.alleleUsagePlotData) : [];
  }

  // group usage data by locus
  private groupDataByGeneType(data: AlleleUsageRow[]) {
    const dataByGeneType: { [key: string]: AlleleUsageRow[] } = {};
    data.forEach((row) => {
      if (!dataByGeneType[row.gene_type]) {
        dataByGeneType[row.gene_type] = [];
      }
      dataByGeneType[row.gene_type].push(row);
    });
    return dataByGeneType;
  }

  // Method to calculate the canvas style to fit the number of genes
  getalleleUsageCanvasStyle(geneType: string): string {
    if (this.alleleUsagePlotData) {
      let geneCount = this.alleleUsagePlotData[geneType].labels.length;

      if (this.searchActive && this.compareSelectedRows) {
        geneCount *= 2;
      }

      const h = 60 + geneCount * 20;
      console.log(`gene type: ${geneType} height: ${h}px;`);
      return `height: ${h}px; width: 60vw`;
    } else {
      return '';
    }
  }

  // calculate the highest gene number for this gene
  private calculateMaxAllele(geneTypeData: AlleleUsageRow[]) {
    let maxAllele = 0;
    geneTypeData.forEach((row) => {
      const alleleMatch = row.vdjbase_allele.match(/\*(\d+)/);
      if (alleleMatch) {
        const alleleNumber = parseInt(alleleMatch[1], 10);
        if (alleleNumber > maxAllele) {
          maxAllele = alleleNumber;
        }
      }
    });
    return maxAllele;
  }

  // --- Functions for creating bar chart data

  alleleBarUsageData(data: AlleleUsageRow[]) {
    // Create a structure to group data by gene_type
    const dataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(data);

    const result: {
      [key: string]: BarData;
    } = {};

    Object.keys(dataByGeneType).forEach((geneType) => {
      const geneTypeData = dataByGeneType[geneType];

      // Find the maximum allele prefix for this gene type
      let maxAllele = this.calculateMaxAllele(geneTypeData);

      // Create the structure for this gene type
      const genes = [...new Set(geneTypeData.map((row) => row.gene))].sort();
      const datasets = this.buildBarDatasets(geneTypeData, genes, maxAllele);
      const labels = genes;

      result[geneType] = {
        labels,
        datasets,
      };
    });

    return result;
  }

  comparativeAlleleBarUsageData(data: AlleleUsageRow[], removedData: AlleleUsageRow[]) {
    const allData = data.concat(removedData);
    const dataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(data);
    const removedDataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(removedData);
    const allDataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(allData);

    const result: {
      [key: string]: BarData;
    } = {};

    const violinResult: {
      [key: string]: ViolinData;
    } = {};

    Object.keys(dataByGeneType).forEach((geneType) => {
      const allGeneTypeData = allDataByGeneType[geneType];

      // Find the maximum allele prefix for this gene type
      const maxAllele = this.calculateMaxAllele(allGeneTypeData);
      const genes = [...new Set(allGeneTypeData.map((row) => row.gene))].sort();

      // Create the structure for this gene type
      let datasets: BarDataset[] = this.buildBarDatasets(dataByGeneType[geneType], genes, maxAllele, 'Stack 0');
      datasets.push(...this.buildBarDatasets(removedDataByGeneType[geneType], genes, maxAllele, 'Stack 1'));

      const labels = genes;

      result[geneType] = {
        labels,
        datasets,
      };
    });

    return result;
  }

  private buildBarDatasets(
    geneTypeData: AlleleUsageRow[],
    genes: string[],
    maxAllele: number,
    stack: string = 'stack',
  ): BarDataset[] {
    const brewerColors = [
      '#a6cee3',
      '#1f78b4',
      '#b2df8a',
      '#33a02c',
      '#fb9a99',
      '#e31a1c',
      '#fdbf6f',
      '#ff7f00',
      '#cab2d6',
      '#6a3d9a',
      '#ffff99',
      '#b15928',
    ];
    const datasets: BarDataset[] = Array.from({ length: maxAllele }, (_, i) => ({
      label: `*${String(i + 1).padStart(2, '0')}`,
      data: Array(genes.length).fill(0),
      barThickness: 10,
      stack: stack,
      backgroundColor: brewerColors[i % brewerColors.length],
    }));

    // Populate the datasets for this gene type
    this.populateAlleleBarDatasets(geneTypeData, genes, datasets);
    return datasets;
  }

  private populateAlleleBarDatasets(
    geneTypeData: AlleleUsageRow[],
    genes: string[],
    datasets: { label: string; data: any[]; barThickness?: number }[],
  ) {
    geneTypeData.forEach((row) => {
      const geneIndex = genes.indexOf(row.gene);
      const alleleMatch = row.vdjbase_allele.match(/\*(\d+)/);
      if (alleleMatch) {
        const alleleNumber = parseInt(alleleMatch[1], 10);
        datasets[alleleNumber - 1].data[geneIndex] += parseFloat(row.frequency);
      }
    });

    // Count the number of unique samples for this gene type
    const uniqueSamples = new Set(geneTypeData.map((row) => row.sample_name)).size;

    // Divide each value in datasets.data by the number of unique samples
    datasets.forEach((dataset) => {
      dataset.data = dataset.data.map((value) => value / uniqueSamples);
    });
  }

  // Chart options for allele usage
  public barChartAlleleUsageOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    indexAxis: 'y',
    maintainAspectRatio: false,
    elements: {},
    scales: {
      x: {
        display: true,
        title: { text: 'Frequency', display: true },
        stacked: true,
      },
      y: {
        display: true,
        stacked: true,
      },
    },
  };

  // --- Functions for creating violin plot data

  private alleleViolinUsageData(data: AlleleUsageRow[]) {
    // Create a structure to group data by gene_type
    const dataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(data);

    const result: {
      [key: string]: ViolinData;
    } = {};

    Object.keys(dataByGeneType).forEach((geneType) => {
      const geneTypeData = dataByGeneType[geneType];

      // Create the structure for this gene type
      const labels = [...new Set(geneTypeData.map((row) => row.gene))].sort();

      const datasets = [
        {
          label: 'Selected',
          data: this.buildAlleleViolinData(geneTypeData, labels),
        },
      ];

      result[geneType] = {
        labels,
        datasets,
      };
    });

    return result;
  }

  private comparativeAlleleViolinUsageData(data: AlleleUsageRow[], removedData: AlleleUsageRow[]) {
    const allData = data.concat(removedData);
    const dataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(data);
    const removedDataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(removedData);
    const allDataByGeneType: { [key: string]: AlleleUsageRow[] } = this.groupDataByGeneType(allData);

    const result: {
      [key: string]: ViolinData;
    } = {};

    Object.keys(dataByGeneType).forEach((geneType) => {
      const allGeneTypeData = allDataByGeneType[geneType];
      const geneTypeData = dataByGeneType[geneType];
      const removedGeneTypeData = removedDataByGeneType[geneType];

      // Create the structure for this gene type
      const labels = [...new Set(allGeneTypeData.map((row) => row.gene))].sort();

      const datasets = [
        {
          label: 'Selected',
          data: this.buildAlleleViolinData(geneTypeData, labels),
        },
        {
          label: 'Unselected',
          data: this.buildAlleleViolinData(removedGeneTypeData, labels),
        },
      ];

      result[geneType] = {
        labels,
        datasets,
      };
    });

    return result;
  }

  private buildAlleleViolinData(geneTypeData: AlleleUsageRow[], genes: string[]) {
    const data: number[][] = Array.from({ length: genes.length }, () => []);

    geneTypeData.forEach((row) => {
      console.log(row);
      const geneIndex = genes.indexOf(row.gene);
      data[geneIndex].push(parseFloat(row.frequency));
    });

    return data;
  }

  public violinChartAlleleUsageOptions: ChartConfiguration<'violin'>['options'] = {
    responsive: true,
    indexAxis: 'y',
    maintainAspectRatio: false,
    elements: {
      violin: {
        itemRadius: 2,
        itemHitRadius: 4,
        itemBackgroundColor: 'black',
        itemBorderColor: 'white',
      },
    },
    scales: {
      x: {
        display: true,
        title: { text: 'Frequency', display: true },
      },
      y: {
        display: true,
      },
    },
  };
}
